import { NurseLayout } from "@/components/layout/nurse-layout";
import { useParams, Link } from "wouter";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle, Clock, Circle, ChevronRight, Baby } from "lucide-react";

type PracticeLevel = "specialist" | "advanced" | "consultant";
const LEVEL_KEY = "rheumhub_practice_level";

const STATUS_CONFIG = {
  achieved: { label: "Achieved", icon: CheckCircle, color: "text-green-600 bg-green-50 border-green-200" },
  in_progress: { label: "In Progress", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200" },
  not_started: { label: "Not Started", icon: Circle, color: "text-slate-400 bg-slate-50 border-slate-200" },
  null: { label: "Not Started", icon: Circle, color: "text-slate-400 bg-slate-50 border-slate-200" },
};

interface Statement {
  id: number;
  code: string;
  statement: string;
  specialistLevel: string;
  advancedLevel: string;
  consultantLevel: string;
  isPaediatric: boolean;
  sortOrder: number;
  portfolioEntry: { status: string; practiceLevel: string } | null;
}

interface Domain {
  id: number;
  code: string;
  title: string;
  description: string;
}

export default function CompetencyDomain() {
  const params = useParams<{ id: string }>();
  const domainId = params.id;
  const [level, setLevel] = useState<PracticeLevel>(
    () => (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist"
  );
  const [domain, setDomain] = useState<Domain | null>(null);
  const [statements, setStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [domainsRes, stmtsRes] = await Promise.all([
          fetch("/api/competency/domains"),
          fetch(`/api/competency/domains/${domainId}/statements`),
        ]);
        if (domainsRes.ok) {
          const domains = await domainsRes.json();
          setDomain(domains.find((d: Domain) => d.id === parseInt(domainId)));
        }
        if (stmtsRes.ok) setStatements(await stmtsRes.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [domainId]);

  const levelText = (s: Statement) => {
    if (level === "specialist") return s.specialistLevel;
    if (level === "advanced") return s.advancedLevel;
    return s.consultantLevel;
  };

  const filtered = statements.filter((s) => {
    const status = s.portfolioEntry?.status ?? "not_started";
    if (filter !== "all" && status !== filter) return false;
    if (search && !s.statement.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <NurseLayout title={domain?.title ?? "Domain"}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/nurse/competency">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              All Domains
            </Button>
          </Link>
          {domain && (
            <div>
              <span className="text-xs font-mono text-slate-400">{domain.code}</span>
              <p className="text-sm text-slate-600">{domain.description}</p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search statements..."
            className="flex-1 min-w-[200px] h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {["all", "not_started", "in_progress", "achieved"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  filter === f ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {f === "all" ? "All" : f === "not_started" ? "Not Started" : f === "in_progress" ? "In Progress" : "Achieved"}
              </button>
            ))}
          </div>
        </div>

        {/* Statement List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No statements match your filter.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((s) => {
              const status = (s.portfolioEntry?.status ?? "not_started") as keyof typeof STATUS_CONFIG;
              const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_started;
              const Icon = cfg.icon;
              return (
                <Link key={s.id} href={`/nurse/competency/${domainId}/${s.id}`}>
                  <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer group">
                    <div className="flex items-start gap-4">
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium shrink-0 mt-0.5 ${cfg.color}`}>
                        <Icon className="h-3 w-3" />
                        {cfg.label}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-400">{s.code}</span>
                          {s.isPaediatric && (
                            <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                              <Baby className="h-3 w-3" />
                              Paediatric
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-slate-900 mb-1 group-hover:text-teal-700 transition-colors">{s.statement}</p>
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{levelText(s)}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-teal-500 shrink-0 mt-2 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </NurseLayout>
  );
}
